import React, {useState, useEffect, PropsWithChildren, createContext} from 'react';

import {
    SubscriptionPlanContext,
    ConsumerSubscriptionPlan,
    ConsumerSubscriptionValuePlan,
    useConsumerSubscriptionPlan,
    useConsumerSubscriptionValuePlan,
} from '@udemy/subscription-graphql';

import {useGetAcceleratorEnrollmentQuery} from 'src/generated/graphql';

import {checkCourseSubscription} from '../../utils/check-course-subscription';
import {coursesInSubscriptionPlan} from '../../utils/courses-in-subscription-plan';

export enum CareerAcceleratorCTAType {
    LOADING,
    GO_TO_COURSES_MX,
    GO_TO_COURSES_PP,
    ENROLL_IN_COURSES,
    /* NOTE: Right now the PP purchase CTA is unused, and no-one should be able to see it.
     * This was done because we changed the MX CTA to a PP CTA, then reverted back to the MX CTA...
     * Keeping the PP CTA option around, just in case we need it again.
     *
     * If it's becoming a maintenance burden, feel freeo to remove it.
     */
    PURCHASE_CTA_PP,
    PURCHASE_CTA_MX,
}

export const getCTAType = (
    isSubscribedToAllCourses: boolean,
    subscriptionPlanIncludesAllCourses: boolean,
    personalPlanContext: SubscriptionPlanContext<ConsumerSubscriptionPlan>,
    valuePlanContext: SubscriptionPlanContext<ConsumerSubscriptionValuePlan>,
    isLoading: boolean,
    isEnrolledInAccelerator: boolean,
) => {
    if (isLoading) {
        return CareerAcceleratorCTAType.LOADING;
    }

    const isPPSubscriber =
        personalPlanContext &&
        'isSubscriberToSpecificPlan' in personalPlanContext &&
        personalPlanContext.isSubscriberToSpecificPlan;

    const isValuePlanSubscriber =
        valuePlanContext &&
        'isSubscriberToSpecificPlan' in valuePlanContext &&
        valuePlanContext.isSubscriberToSpecificPlan;

    // If the user is enrolled in all courses but not in PP or VP, take them to the my learning page.
    if (isSubscribedToAllCourses && !isPPSubscriber && !isValuePlanSubscriber) {
        return CareerAcceleratorCTAType.GO_TO_COURSES_MX;
    }

    // On the other hand, if the user is a PP/VP subscriber, then check if they are enrolled in the academy.
    // If yes, take them to the my learning page.
    // If not:
    // - If any courses are excluded from the subscription plan, let them purchase the courses.
    // - If the courses are all included in the subscription plan, let them enroll.
    if (isPPSubscriber || isValuePlanSubscriber) {
        if (isEnrolledInAccelerator) {
            return CareerAcceleratorCTAType.GO_TO_COURSES_PP;
        } else if (!subscriptionPlanIncludesAllCourses) {
            return CareerAcceleratorCTAType.PURCHASE_CTA_MX;
        } else {
            return CareerAcceleratorCTAType.ENROLL_IN_COURSES;
        }
    }

    // We should only reach this point if the PP experiment is off or PP is unavailable, so show the AddToCart button.
    return CareerAcceleratorCTAType.PURCHASE_CTA_MX;
};

interface CTAContextValue {
    ctaType: CareerAcceleratorCTAType;
}

export const CTAContext = createContext<CTAContextValue>({
    ctaType: CareerAcceleratorCTAType.LOADING,
});

interface CTAContextProviderProps {
    courseIds: number[];
    acceleratorId: number;
}

export const CTAContextProvider = ({
    acceleratorId,
    courseIds,
    children,
}: PropsWithChildren<CTAContextProviderProps>) => {
    const [courseSubscriptionData, setCourseSubscriptionData] = useState({
        isSubscribedToAllCourses: false,
        subscriptionPlanIncludesAllCourses: false,
        isLoading: true,
    });
    const [acceleratorEnrollment, setAcceleratorEnrollment] = useState({
        isEnrolledInAccelerator: false,
        isLoading: true,
    });
    const personalPlanContext = useConsumerSubscriptionPlan();
    const valuePlanContext = useConsumerSubscriptionValuePlan();

    const isLoading =
        valuePlanContext?.isLoading ||
        personalPlanContext?.isLoading ||
        courseSubscriptionData.isLoading ||
        acceleratorEnrollment.isLoading;

    const ctaType = getCTAType(
        courseSubscriptionData.isSubscribedToAllCourses,
        courseSubscriptionData.subscriptionPlanIncludesAllCourses,
        personalPlanContext,
        valuePlanContext,
        isLoading,
        acceleratorEnrollment.isEnrolledInAccelerator,
    );

    const value = {
        ctaType,
    };

    useEffect(() => {
        if (courseIds?.length) {
            const checkSubscriptions = async () => {
                const subscriptionStatus = await checkCourseSubscription(courseIds);
                const subscriptionPlanIncludesAllCourses =
                    await coursesInSubscriptionPlan(courseIds);
                setCourseSubscriptionData({
                    isSubscribedToAllCourses: subscriptionStatus,
                    subscriptionPlanIncludesAllCourses: subscriptionPlanIncludesAllCourses,
                    isLoading: false,
                });
            };

            checkSubscriptions();
        }
    }, [courseIds]);

    useEffect(() => {
        const checkAcceleratorEnrollment = async () => {
            const isEnrolledInAccelerator = (
                await useGetAcceleratorEnrollmentQuery.fetcher({id: acceleratorId.toString()})()
            ).careerAccelerator?.isEnrolled;
            setAcceleratorEnrollment({
                isEnrolledInAccelerator: isEnrolledInAccelerator ?? false,
                isLoading: false,
            });
        };

        checkAcceleratorEnrollment();
    }, [acceleratorId]);

    return <CTAContext.Provider value={value}>{children}</CTAContext.Provider>;
};
